import { useCallback, useMemo, useRef, useState } from 'react';
import { useOrganizationId } from '@features/user';
import { formatDistanceToNow } from 'date-fns';

import type { DeliveryHistoryEntry } from '../components/DeliveryHistoryList';
import type { RequirementRowView } from '../components/PatientRequirementsSectionView';
import {
  requestedDetail,
  resolveDisplayState,
  sendActionLabel,
  touchpointTimestamp,
} from '../vocabulary';

import {
  useGetPatientRequirementsQuery,
  useGetRequirementDeliveryHistoryQuery,
  useReopenRequirementMutation,
  useSendRequirementRequestMutation,
  useWaiveRequirementMutation,
} from './api';

/**
 * Lift the secure link out of a touchpoint payload for AC-20's anchor.
 *
 * The payload is the touchpoint's metadata, which the backend builds from the
 * notification template data (`{ link, is_reminder }`) — so `link` is the
 * expected key. It is typed as an open map though, so rather than trust one
 * name this falls back to the first URL-looking value. Since GDN-3398 the
 * payload is no longer rendered, so missing the link costs the anchor outright
 * — the row still says what was sent and whether it arrived.
 */
const secureLinkFrom = (
  payload: Record<string, unknown> | undefined,
): string | undefined => {
  if (!payload) return undefined;
  const named = payload.link;
  if (typeof named === 'string' && named.startsWith('http')) return named;
  return Object.values(payload).find(
    (value): value is string => typeof value === 'string' && value.startsWith('http'),
  );
};

const relative = (iso: string | null | undefined): string | undefined => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return formatDistanceToNow(parsed, { addSuffix: true });
};

export interface PatientRequirementsResult {
  rows: RequirementRowView[];
  /** Server-computed (AC-3); not re-derived here, so one rule decides it. */
  missingCount: number;
  deliveryHistory: DeliveryHistoryEntry[];
  /**
   * The history request failed. Kept separate from an empty list on purpose:
   * "we cannot show you what was sent" and "nothing has been sent" lead staff
   * to opposite conclusions, and the absent-data mapping made them identical.
   */
  isHistoryError: boolean;
  /** Retries the history alone; the requirement rows are unaffected. */
  retryHistory: () => void;
  isLoading: boolean;
  isError: boolean;
  /**
   * Requirement ids with a write in flight. A SET, not a single id: two rows
   * can be acted on at once, and a scalar would let whichever finished first
   * re-enable the other row while its own write was still pending.
   */
  busyRequirementIds: ReadonlySet<string>;
  refetch: () => void;
  send: (patientRequirementId: string) => Promise<void>;
  waive: (patientRequirementId: string, reason: string) => Promise<void>;
  reopen: (patientRequirementId: string) => Promise<void>;
}

/**
 * The Requirements section's data (GDN-3226; AC-3, AC-4, AC-8, AC-20).
 *
 * Every displayed fact now comes off the row itself — `label`, `kind`,
 * `has_active_session`, `last_requested_at` — so this no longer joins the org
 * catalog to name a row. The delivery history is still fetched, but only for
 * the AC-20 panel; the Requested state and the Send/Remind flip read the row,
 * which is authoritative and does not depend on a second request resolving.
 */
export const usePatientRequirements = (
  patientId: string,
  enabled: boolean = true,
): PatientRequirementsResult => {
  const organizationId = useOrganizationId();
  const skip = !enabled || !organizationId || !patientId;

  const {
    data,
    isLoading,
    isError,
    refetch: refetchRows,
  } = useGetPatientRequirementsQuery(
    { orgId: organizationId as string, patientId },
    { skip },
  );

  const {
    data: history,
    isError: isHistoryError,
    refetch: refetchHistory,
  } = useGetRequirementDeliveryHistoryQuery(
    { orgId: organizationId as string, patientId },
    { skip },
  );

  const [sendRequest] = useSendRequirementRequestMutation();
  const [waiveRequirement] = useWaiveRequirementMutation();
  const [reopenRequirement] = useReopenRequirementMutation();

  const [busyRequirementIds, setBusyRequirementIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // The same set, kept in a ref so it can be READ synchronously. State updates
  // are async: two clicks on one row in the same tick both observe the old
  // state and both dispatch. Only a value mutated before the first await can
  // stop the second.
  const inFlightRef = useRef<Set<string>>(new Set());

  const rows = useMemo<RequirementRowView[]>(() => {
    const items = data?.items ?? [];
    return items.map(item => {
      const hasActiveSession = item.has_active_session ?? false;
      const state = resolveDisplayState(item, hasActiveSession);

      let detail: string | undefined;
      if (state === 'waived') {
        // The reason is the whole point of AC-8's audit trail; a bare "Waived"
        // tells the next staff member nothing about why they should not chase.
        detail = item.waive_reason
          ? `Waived — ${item.waive_reason}`
          : 'Waived';
      } else if (state === 'requested') {
        detail = requestedDetail(item.last_request_channel, item.last_requested_at);
      }

      return {
        patientRequirementId: item.patient_requirement_id,
        // The org-facing label, straight off the row. A row with no label is a
        // backend gap, not a reason to render an empty cell.
        label: item.label ?? 'Untitled requirement',
        state,
        detail,
        // AC-4: settled rows have nothing to chase, so no action at all.
        // Outstanding rows always offer it — the server decides whether any
        // channel qualifies, and records a failed attempt when none does.
        sendActionLabel:
          state === 'missing' || state === 'requested'
            ? sendActionLabel(hasActiveSession)
            : undefined,
        canWaive: state === 'missing' || state === 'requested',
        canReopen: state === 'waived',
      };
    });
  }, [data]);

  const deliveryHistory = useMemo<DeliveryHistoryEntry[]>(() => {
    const touchpoints = history?.touchpoints ?? [];
    return [...touchpoints]
      .sort((a, b) =>
        (touchpointTimestamp(b) ?? '').localeCompare(touchpointTimestamp(a) ?? ''),
      )
      .map(touchpoint => {
        const iso = touchpointTimestamp(touchpoint);
        return {
          touchpointId: touchpoint.touchpoint_id,
          channel: touchpoint.channel,
          status: touchpoint.status,
          timestamp: relative(iso),
          timestampIso: iso,
          secureLink: secureLinkFrom(touchpoint.payload),
          // Straight through, null included. GDN-3414: the label is whatever
          // the send recorded, never inferred from `sequence` — the contract
          // says so in as many words, because a send that reached nobody
          // spends a sequence too.
          isReminder: touchpoint.is_reminder,
        };
      });
  }, [history]);

  const refetch = useCallback(() => {
    void refetchRows();
    void refetchHistory();
  }, [refetchRows, refetchHistory]);

  const retryHistory = useCallback(() => {
    void refetchHistory();
  }, [refetchHistory]);

  /**
   * One wrapper for all three writes. Each awaits `.unwrap()` so a rejected
   * write reaches the caller instead of resolving quietly, and the busy id is
   * cleared in `finally` so a failure never strands the row disabled.
   */
  const runWrite = useCallback(
    async (patientRequirementId: string, write: () => Promise<unknown>) => {
      if (!organizationId) return;
      // Synchronous guard, before anything awaits. The disabled button is a
      // rendering consequence of state that has not committed yet, so it is not
      // a guard at all for a double-fire inside one tick.
      if (inFlightRef.current.has(patientRequirementId)) return;
      inFlightRef.current.add(patientRequirementId);

      // Functional updates throughout: two concurrent writes each read and
      // rewrite this set, and a captured copy would drop the sibling's entry.
      setBusyRequirementIds(current => new Set(current).add(patientRequirementId));
      try {
        await write();
      } finally {
        inFlightRef.current.delete(patientRequirementId);
        setBusyRequirementIds(current => {
          const next = new Set(current);
          next.delete(patientRequirementId);
          return next;
        });
      }
    },
    [organizationId],
  );

  const send = useCallback(
    (patientRequirementId: string) =>
      // `channels` is omitted deliberately, not forgotten. The contract reads
      // "omit to derive them from the patient's communication preferences and
      // contact info, which is the normal path"; passing an explicit list would
      // OVERRIDE that derivation, which is exactly what we no longer want.
      runWrite(patientRequirementId, () =>
        sendRequest({
          orgId: organizationId as string,
          patientId,
          sendRequestBody: {},
        }).unwrap(),
      ),
    [runWrite, sendRequest, organizationId, patientId],
  );

  const waive = useCallback(
    (patientRequirementId: string, reason: string) =>
      runWrite(patientRequirementId, () =>
        waiveRequirement({
          orgId: organizationId as string,
          patientId,
          requirementId: patientRequirementId,
          waiveRequest: { reason },
        }).unwrap(),
      ),
    [runWrite, waiveRequirement, organizationId, patientId],
  );

  const reopen = useCallback(
    (patientRequirementId: string) =>
      runWrite(patientRequirementId, () =>
        reopenRequirement({
          orgId: organizationId as string,
          patientId,
          requirementId: patientRequirementId,
        }).unwrap(),
      ),
    [runWrite, reopenRequirement, organizationId, patientId],
  );

  return {
    rows,
    missingCount: data?.missing_count ?? 0,
    deliveryHistory,
    isHistoryError,
    retryHistory,
    isLoading: !skip && isLoading,
    isError,
    busyRequirementIds,
    refetch,
    send,
    waive,
    reopen,
  };
};
